/**
 * Centralized Storage Service
 * Provides a unified abstraction layer for all storage operations
 */

import { logger } from '../utils/logger';

const storageLogger = logger.clone({ labels: { component: 'StorageService' }});

export interface StorageOptions {
  maxAge?: number; // Max age in milliseconds
  namespace?: string; // Key prefix
  fallbackToMemory?: boolean; // Use in-memory storage if browser storage fails
}

interface StorageItem<T> {
  value: T;
  timestamp: number;
  version?: string;
}

class StorageService {
  private memoryStorage: Map<string, StorageItem<unknown>> = new Map();
  private readonly defaultNamespace = 'safepassage';

  /**
   * Get item from storage with type safety
   */
  getItem<T>(key: string, options?: StorageOptions): T | null {
    const fullKey = this.getFullKey(key, options?.namespace);

    try {
      // Try sessionStorage first
      const raw = sessionStorage.getItem(fullKey);
      if (raw) {
        const item = JSON.parse(raw) as StorageItem<T>;

        // Check if item has expired
        if (options?.maxAge && Date.now() - item.timestamp > options.maxAge) {
          this.removeItem(key, options);
          return null;
        }

        return item.value;
      }

      // Fallback to memory storage if enabled
      if (options?.fallbackToMemory) {
        const memItem = this.memoryStorage.get(fullKey);
        if (memItem) {
          if (
            options?.maxAge &&
            Date.now() - memItem.timestamp > options.maxAge
          ) {
            this.memoryStorage.delete(fullKey);
            return null;
          }
          return memItem.value as T;
        }
      }

      return null;
    } catch (error) {
      storageLogger.error('Failed to get item from storage', {
        labels: {
          key: fullKey,
          error: error instanceof Error ? error.message : String(error),
        }
      });
      return null;
    }
  }

  /**
   * Set item in storage with type safety
   */
  setItem<T>(key: string, value: T, options?: StorageOptions): boolean {
    const fullKey = this.getFullKey(key, options?.namespace);
    const item: StorageItem<T> = {
      value,
      timestamp: Date.now(),
      version: '1.0',
    };

    try {
      sessionStorage.setItem(fullKey, JSON.stringify(item));
      return true;
    } catch (error) {
      storageLogger.warn('Failed to set item in sessionStorage', {
        labels: {
          key: fullKey,
          error: error instanceof Error ? error.message : String(error),
        }
      });

      // Fallback to memory storage if enabled
      if (options?.fallbackToMemory) {
        this.memoryStorage.set(fullKey, item);
        return true;
      }

      return false;
    }
  }

  /**
   * Remove item from storage
   */
  removeItem(key: string, options?: StorageOptions): void {
    const fullKey = this.getFullKey(key, options?.namespace);

    try {
      sessionStorage.removeItem(fullKey);
    } catch (error) {
      storageLogger.warn('Failed to remove item from sessionStorage', {
        labels: {
          key: fullKey,
          error: error instanceof Error ? error.message : String(error),
        }
      });
    }

    // Also remove from memory storage
    this.memoryStorage.delete(fullKey);
  }

  /**
   * Clear all items with optional namespace filter
   */
  clear(namespace?: string): void {
    const prefix = namespace || this.defaultNamespace;

    // Clear sessionStorage
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key?.startsWith(prefix)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => sessionStorage.removeItem(key));
    } catch (error) {
      storageLogger.error('Failed to clear sessionStorage:', error instanceof Error ? error : new Error(`${error}`));
    }

    // Clear memory storage
    Array.from(this.memoryStorage.keys())
      .filter((key) => key.startsWith(prefix))
      .forEach((key) => this.memoryStorage.delete(key));
  }

  /**
   * Get all keys with optional namespace filter
   */
  getAllKeys(namespace?: string): string[] {
    const prefix = namespace || this.defaultNamespace;
    const keys: string[] = [];

    // Get from sessionStorage
    try {
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key?.startsWith(prefix)) {
          keys.push(key.substring(prefix.length + 1)); // Remove namespace prefix
        }
      }
    } catch (error) {
      storageLogger.error('Failed to get keys from sessionStorage:', error instanceof Error ? error : new Error(`${error}`));
    }

    // Get from memory storage
    Array.from(this.memoryStorage.keys())
      .filter((key) => key.startsWith(prefix))
      .forEach((key) => {
        const shortKey = key.substring(prefix.length + 1);
        if (!keys.includes(shortKey)) {
          keys.push(shortKey);
        }
      });

    return keys;
  }

  /**
   * Check if storage is available
   */
  isAvailable(): boolean {
    try {
      const testKey = '__storage_test__';
      sessionStorage.setItem(testKey, 'test');
      sessionStorage.removeItem(testKey);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Get storage size in bytes
   */
  getSize(namespace?: string): number {
    const prefix = namespace || this.defaultNamespace;
    let size = 0;

    try {
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key?.startsWith(prefix)) {
          const value = sessionStorage.getItem(key);
          if (value) {
            size += key.length + value.length;
          }
        }
      }
    } catch (error) {
      storageLogger.error('Failed to calculate storage size:', error instanceof Error ? error : new Error(`${error}`));
    }

    return size;
  }

  private getFullKey(key: string, namespace?: string): string {
    const prefix = namespace || this.defaultNamespace;
    return `${prefix}_${key}`;
  }
}

// Export singleton instance
export const storageService = new StorageService();

// Export storage keys enum for type safety
export enum StorageKeys {
  SESSION_STATE = 'verification_session',
  SELFIE_DATA = 'verification_selfie',
  CONFIG_OVERRIDES = 'config_overrides',
  RECOVERY_DATA = 'recovery_data',
  PERFORMANCE_CACHE = 'performance_cache',
  PENDING_ENGINE_RESULT = 'pending_engine_result',
}
